import React, { useState } from 'react';
import { ethers } from 'ethers';
import Card from '../Common/Card';
import Button from '../Common/Button';
import Input from '../Common/Input';
import { useWeb3 } from '../../context/Web3Context';
import { notify, removeNotify } from '../../utils/notify';
import { parseContractError } from '../../utils/errors';
import { formatUSDT, parseUSDT, bpsToPercent, pctToBps } from '../../utils/format';

// OWNER-ONLY section. Render guard lives in AdminPanel — this component
// shouldn't be mounted for non-owner accounts.

const RouterPool = ({ routerPool, routerPoolFee, onChange }) => {
  const { contract } = useWeb3();

  const [to, setTo] = useState('');
  const [amount, setAmount] = useState('');
  const [feePct, setFeePct] = useState('');
  const [busy, setBusy] = useState('');

  const withdraw = async () => {
    if (!contract) return;
    if (!ethers.utils.isAddress(to)) {
      notify.error('Invalid destination address');
      return;
    }
    const parsed = parseUSDT(amount);
    if (parsed.lte(0)) {
      notify.error('Enter a valid amount');
      return;
    }
    setBusy('withdraw');
    let lid = null;
    try {
      lid = notify.loading('Withdrawing from router pool...');
      const tx = await contract.withdrawFromRouterPool(to, parsed);
      await tx.wait();
      removeNotify(lid); lid = null;
      notify.success('Router pool withdrawal complete');
      setAmount(''); setTo('');
      onChange && onChange();
    } catch (err) {
      if (lid) removeNotify(lid);
      notify.error(parseContractError(err));
    } finally {
      setBusy('');
    }
  };

  const updateFee = async () => {
    if (!contract) return;
    setBusy('fee');
    let lid = null;
    try {
      lid = notify.loading('Updating router pool fee...');
      const tx = await contract.updateRouterPoolFee(pctToBps(feePct));
      await tx.wait();
      removeNotify(lid); lid = null;
      notify.success('Router pool fee updated');
      setFeePct('');
      onChange && onChange();
    } catch (err) {
      if (lid) removeNotify(lid);
      notify.error(parseContractError(err));
    } finally {
      setBusy('');
    }
  };

  return (
    <Card
      title="Router Pool"
      subtitle="Owner only · Confidential"
      glow
    >
      <div style={{
        padding: 18,
        background: 'linear-gradient(135deg, rgba(184, 85, 255, 0.1), rgba(0, 212, 255, 0.05))',
        border: '1px solid rgba(184, 85, 255, 0.3)',
        borderRadius: 'var(--radius-md)',
        marginBottom: 20
      }}>
        <div className="stat-label">Pool Balance</div>
        <div style={{
          fontFamily: 'JetBrains Mono',
          fontSize: 32,
          fontWeight: 700,
          color: 'var(--accent-purple)',
          textShadow: '0 0 24px rgba(184, 85, 255, 0.4)'
        }}>
          {formatUSDT(routerPool || '0')} <span className="stat-suffix">USDT</span>
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
          Current router pool fee: {bpsToPercent(routerPoolFee || 0)}%
        </div>
      </div>

      <Input
        label="Destination Wallet"
        value={to}
        onChange={(e) => setTo(e.target.value)}
        placeholder="0x..."
      />

      <Input
        label="Amount"
        value={amount}
        onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
        placeholder="0.00"
        suffix="USDT"
      />

      <Button
        variant="primary"
        size="lg"
        block
        loading={busy === 'withdraw'}
        onClick={withdraw}
        disabled={!to || !amount}
      >Withdraw Router Pool</Button>

      <div style={{
        marginTop: 24,
        paddingTop: 20,
        borderTop: '1px solid var(--border-secondary)'
      }}>
        <Input
          label={`Router Pool Fee (current ${bpsToPercent(routerPoolFee || 0)}%)`}
          value={feePct}
          onChange={(e) => setFeePct(e.target.value.replace(/[^0-9.]/g, ''))}
          placeholder="e.g. 1"
          suffix="%"
          helper="Max 10%"
        />
        <Button
          variant="ghost"
          size="sm"
          block
          loading={busy === 'fee'}
          onClick={updateFee}
          disabled={!feePct}
        >Update Router Fee</Button>
      </div>
    </Card>
  );
};

export default RouterPool;
